import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useStores } from 'hooks/useStores'
import { getRoleNameFromId } from 'utils/auth/role'
import { AuthRoleNameEnum } from 'constants/user'
import routes from 'routes'
import {
  adminSidebarItems,
  SidebarItem,
  sidebarItems
} from 'components/LeftSidebar/components/SideBarContent/constants'

interface GatedPath {
  path: string
  allowedRoles: AuthRoleNameEnum[]
}

// Flatten the sidebar tree into the role-gated leaf paths. A path is gated when it
// is a real route (not the '/' group-container marker) and declares allowedRoles.
// Deriving from the sidebar constants keeps this guard in lock-step with sidebar
// visibility — the same rule that hides an item also blocks its route.
const collectGatedPaths = (items: SidebarItem[]): GatedPath[] => {
  const out: GatedPath[] = []
  const walk = (list: SidebarItem[]): void => {
    for (const item of list) {
      if (item.path && item.path !== '/' && item.allowedRoles?.length) {
        out.push({ path: item.path, allowedRoles: item.allowedRoles })
      }
      if (item.children) walk(item.children)
    }
  }
  walk(items)
  return out
}

const GATED_PATHS = collectGatedPaths([...sidebarItems, ...adminSidebarItems])

// Longest path first so a nested route (e.g. /settings/tag) is matched before a
// shorter prefix sibling.
GATED_PATHS.sort((a, b) => b.path.length - a.path.length)

const matchesGatedPath = (pathname: string, gatedPath: string): boolean =>
  pathname === gatedPath || pathname.startsWith(`${gatedPath}/`)

/**
 * Bounce the user to the dashboard when they sit on a role-gated route their
 * current cross-team role can no longer satisfy. Triggered on refresh/direct hit
 * after a membership change (e.g. an owner removes a coach from a team — the
 * coach's maxRoleAcrossTeams drops to PLAYER, so coach-only pages like /groups or
 * /users are no longer permitted and should fall back to /dashboard).
 *
 * Mirrors sidebar visibility exactly via maxRoleAcrossTeams; BE stays the
 * per-action authorization source of truth.
 */
export const useRoleRouteGuard = (): void => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { authStore } = useStores()
  // Read during render so MobX tracks them and the effect re-runs on change.
  const isReady = authStore.isAuthBootstrapped
  const roleName = getRoleNameFromId(authStore.maxRoleAcrossTeams)

  useEffect(() => {
    // Wait for membership bootstrap — maxRoleAcrossTeams falls back to PLAYER
    // before memberships load, which would bounce a legitimate coach mid-boot.
    if (!isReady) return

    const gated = GATED_PATHS.find(entry => matchesGatedPath(pathname, entry.path))
    if (!gated) return

    const isAllowed = roleName
      ? gated.allowedRoles.some(role => role.toLowerCase() === roleName.toLowerCase())
      : false

    if (!isAllowed) {
      navigate(routes.dashboard.value, { replace: true })
    }
  }, [pathname, isReady, roleName, navigate])
}
